import styled, { css } from 'styled-components'

type StyledSingleImageUploaderProps = {
	size: number
	isDragging: boolean
	isUploading: boolean
	hasImage: boolean
}

export const StyledSingleImageUploaderWrapper = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 0.75rem;

	.uploader__tip {
		margin: 0;
		font-size: 0.7rem;
		color: #4b5563;
		text-align: center;
		max-width: 220px;
		line-height: 1.35;
	}
`

const dragState = css<StyledSingleImageUploaderProps>`
	border-color: ${({ isDragging }) => (isDragging ? '#0080fe' : 'rgba(209, 213, 219, 0.8)')};
	background: ${({ isDragging }) =>
		isDragging ? 'rgba(0, 128, 254, 0.08)' : 'rgba(249, 250, 251, 0.8)'};
	box-shadow: ${({ isDragging }) =>
		isDragging ? '0 0 0 4px rgba(0, 128, 254, 0.15), 0 12px 24px rgba(15, 23, 42, 0.08)' : 'none'};
`

const StyledSingleImageUploader = styled.div<StyledSingleImageUploaderProps>`
	position: relative;
	width: ${({ size }) => `${size}px`};
	height: ${({ size }) => `${size}px`};
	border-radius: 24px;
	overflow: hidden;
	cursor: pointer;
	transition: all 0.2s ease-in-out;

	.uploader__dropzone {
		position: relative;
		width: 100%;
		height: 100%;
		border-radius: inherit;
		display: flex;
		align-items: center;
		justify-content: center;
		border: 2px dashed rgba(209, 213, 219, 0.8);
		background: ${({ hasImage }) => (hasImage ? '#f8fafc' : 'rgba(248, 248, 249, 0.75)')};
		transition: all 0.2s ease-in-out;
		overflow: hidden;
		position: relative;
		color: #1f2937;
		${dragState}

		&::after {
			content: '';
			position: absolute;
			inset: 0;
			background: rgba(31, 41, 55, 0.55);
			opacity: 0;
			transition: opacity 0.2s ease-in-out;
		}
	}

	&:hover .uploader__dropzone::after {
		opacity: ${({ isUploading }) => (isUploading ? 0.6 : 0.35)};
	}

	.uploader__image {
		width: 100%;
		height: 100%;
		object-fit: cover;
		border-radius: inherit;
	}

	.uploader__placeholder {
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 0.75rem;
		color: #6b7280;
		text-align: center;
		padding: 1rem;

		svg {
			font-size: ${({ size }) => `${Math.min(size * 0.35, 56)}px`};
			color: #0080fe;
		}

		span {
			font-size: 0.95rem;
			font-weight: 500;
			color: #1f2937;
		}

		p {
			font-size: 0.8125rem;
			color: #6b7280;
			margin: 0;
		}
	}

	.uploader__overlay {
		position: absolute;
		inset: 0;
		display: flex;
		align-items: center;
		justify-content: center;
		padding: 0.75rem;
		border-radius: inherit;
		background: ${({ isUploading }) =>
			isUploading ? 'rgba(15, 23, 42, 0.8)' : 'rgba(31, 41, 55, 0.75)'};
		color: #f8fafc;
		opacity: ${({ isUploading }) => (isUploading ? 1 : 0)};
		transition: opacity 0.2s ease-in-out;
		font-size: 0.95rem;
		font-weight: 600;
		letter-spacing: 0.01em;
		text-align: center;
	}

	&:hover .uploader__overlay {
		opacity: ${({ isUploading }) => (isUploading ? 1 : 0.85)};
	}

	.uploader__status {
		position: absolute;
		left: 50%;
		bottom: 12px;
		transform: translateX(-50%);
		background: rgba(17, 24, 39, 0.85);
		color: #f8fafc;
		padding: 0.35rem 0.75rem;
		border-radius: 999px;
		font-size: 0.75rem;
		font-weight: 500;
		backdrop-filter: blur(12px);
		box-shadow: 0 8px 20px rgba(15, 23, 42, 0.2);
	}

	.uploader__error {
		margin-top: 0.75rem;
		font-size: 0.8125rem;
		color: #dc2626;
		text-align: center;
	}

	.uploader__widget {
		display: none;
	}
`

export default StyledSingleImageUploader
